import { useLazyQuery, useMutation } from "@apollo/client";
import { Toast } from "@aurem/ui";
import { useI18n } from "providers/I18nProvider";
import { useCallback, useState } from "react";
import { AssistantModeEnum, gql } from "types";
import { ThreadMessage } from "..";

const SEND_MESSAGE = gql(/* GraphQL */ `
  mutation SendMessage($input: MessageInput!) {
    sendMessage(input: $input) {
      isProcessing
      threadId
      workflowId
      userMessage {
        id
        from
        content
        status
        errorMetadata {
          type
          error
        }
        retryCount
        exportUrl
        exportFilename
        exportSize
        metadata {
          __typename
          ... on AssistantMessageMetadata {
            originalMessageId
            result {
              type
              result {
                __typename
                ... on DatabaseExportResult {
                  type
                  reasoning
                  summary
                }
                ... on DatabaseQueryResult {
                  type
                  reasoning
                  summary
                  data
                  totalCount
                }
                ... on ChatResult {
                  type
                  reasoning
                  response
                }
              }
            }
          }
        }
        createdAt
        completedAt
        threadId
      }
    }
  }
`);

const FIND_THREAD = gql(/* GraphQL */ `
  query FindThread($id: String!, $pagination: Pagination) {
    findThread(id: $id) {
      id
      title
      description
      createdAt
      updatedAt
      isActive
      user {
        id
      }
      messagesList(pagination: $pagination) {
        id
        from
        content
        status
        errorMetadata {
          type
          error
        }
        retryCount
        createdAt
        completedAt
        threadId
        exportUrl
        exportFilename
        exportSize
        metadata {
          __typename
          ... on AssistantMessageMetadata {
            originalMessageId
            result {
              type
              result {
                __typename
                ... on DatabaseExportResult {
                  type
                  reasoning
                  summary
                  debug {
                    executionTrace
                    promptEnhancements
                  }
                }
                ... on DatabaseQueryResult {
                  type
                  reasoning
                  summary
                  data
                  totalCount
                  debug {
                    executionTrace
                    promptEnhancements
                  }
                }
                ... on ChatResult {
                  type
                  reasoning
                  response
                }
              }
            }
          }
        }
      }
    }
  }
`);

const CANCEL_MESSAGE = gql(/* GraphQL */ `
  mutation CancelMessage($messageId: String!) {
    cancelMessage(messageId: $messageId) {
      id
      status
      errorMetadata {
        type
        error
      }
      completedAt
    }
  }
`);

const GET_STREAM_STATE = gql(/* GraphQL */ `
  query GetStreamStateQuery($workflowId: String!) {
    getStreamStateQuery(workflowId: $workflowId) {
      stage
      reasoningText
      processingStatus
      dataResult
      isComplete
    }
  }
`);

export const MESSAGES_PAGE_SIZE = 20;

export function useThreadChat() {
  const { t } = useI18n();
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [threadError, setThreadError] = useState<string | null>(null);

  const [doSendMessage] = useMutation(SEND_MESSAGE);
  const [doCancelMessage] = useMutation(CANCEL_MESSAGE);
  const [fetchThread] = useLazyQuery(FIND_THREAD, {
    fetchPolicy: "network-only",
  });
  const [fetchStreamState] = useLazyQuery(GET_STREAM_STATE, {
    fetchPolicy: "no-cache",
  });

  const sendMessage = useCallback(
    async ({
      threadId,
      content,
      retryCount,
      mode,
    }: {
      threadId?: string;
      content: string;
      retryCount?: number;
      mode: AssistantModeEnum;
    }) => {
      const res = await doSendMessage({
        variables: {
          input: { threadId: threadId ?? "", content, retryCount, mode },
        },
      });

      if (!res.data?.sendMessage?.userMessage) {
        throw new Error("Message send failed");
      }

      const {
        threadId: returnedThreadId,
        userMessage,
        workflowId,
      } = res.data.sendMessage;

      return { threadId: returnedThreadId, userMessage, workflowId };
    },
    [doSendMessage],
  );

  const cancelMessage = useCallback(
    async (messageId: string) => {
      const res = await doCancelMessage({ variables: { messageId } });
      const canceled = res.data?.cancelMessage;
      if (!canceled) throw new Error("Cancel message failed");
      return canceled;
    },
    [doCancelMessage],
  );

  // Used for initial thread load — shows loading state to the user
  const getThread = useCallback(
    async (threadId: string) => {
      try {
        setIsLoadingThread(true);
        setThreadError(null);
        const res = await fetchThread({
          variables: {
            id: threadId,
            pagination: { limit: MESSAGES_PAGE_SIZE, offset: 0 },
          },
        });
        if (!res.data?.findThread) {
          throw new Error("Thread not found");
        }
        return res.data.findThread;
      } catch (error) {
        setThreadError(
          t("apps.admin.chatAI.messages.errors.toast.loadingFailed"),
        );
        Toast.error({
          title: t("apps.admin.chatAI.messages.errors.toast.loadingFailed"),
        });
        throw error;
      } finally {
        setIsLoadingThread(false);
      }
    },
    [fetchThread, t],
  );

  // Used for background syncs — no loading state, no error toast
  const fetchThreadSilent = useCallback(
    async (threadId: string) => {
      const res = await fetchThread({
        variables: {
          id: threadId,
          pagination: { limit: MESSAGES_PAGE_SIZE, offset: 0 },
        },
      });
      return res.data?.findThread ?? null;
    },
    [fetchThread],
  );

  const loadMoreMessages = useCallback(
    async (threadId: string, offset: number): Promise<ThreadMessage[]> => {
      const res = await fetchThread({
        variables: {
          id: threadId,
          pagination: { limit: MESSAGES_PAGE_SIZE, offset },
        },
      });
      if (!res.data?.findThread) return [];
      return res.data.findThread.messagesList;
    },
    [fetchThread],
  );

  const getStreamState = useCallback(
    async (workflowId: string) => {
      if (!workflowId) return null;
      const res = await fetchStreamState({ variables: { workflowId } });
      return res.data?.getStreamStateQuery ?? null;
    },
    [fetchStreamState],
  );

  return {
    isLoadingThread,
    threadError,
    setThreadError,
    sendMessage,
    getThread,
    fetchThreadSilent,
    loadMoreMessages,
    cancelMessage,
    getStreamState,
  };
}
